import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { AccountDoc, DealTable } from '@/lib/types';
import { ScoreChip } from '@/components/ScoreChip';
import { SmartTable } from '@/components/smartTable/SmartTable';
import { useAccountsPipeline } from '@/lib/useAccountsPipeline';
import { useAuth } from '@/lib/auth';

type ViewMode = 'smart' | 'dense';

const VIEW_STORAGE_KEY = 'deal-analyzer:accountsView';

function readStoredView(): ViewMode {
  if (typeof window === 'undefined') return 'smart';
  const v = window.localStorage.getItem(VIEW_STORAGE_KEY);
  return v === 'dense' ? 'dense' : 'smart';
}

export function AccountsPage() {
  const [view, setView] = useState<ViewMode>(readStoredView);

  useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_STORAGE_KEY, view);
    } catch {
      /* ignore quota / disabled */
    }
  }, [view]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <ViewToggle value={view} onChange={setView} />
      </div>
      {view === 'smart' ? <SmartView /> : <DenseView />}
    </div>
  );
}

function ViewToggle({
  value,
  onChange,
}: {
  value: ViewMode;
  onChange: (v: ViewMode) => void;
}) {
  const opts: { id: ViewMode; label: string }[] = [
    { id: 'smart', label: 'Smart' },
    { id: 'dense', label: 'Dense' },
  ];
  return (
    <div className="inline-flex gap-0.5 rounded-md bg-slate-100 p-0.5 text-xs font-medium">
      {opts.map((o) => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          className={`rounded px-3 py-1 transition ${
            value === o.id
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Smart view — uses the pipeline adapter + design-spec components.
// ---------------------------------------------------------------------------
function SmartView() {
  const { user } = useAuth();
  const { accounts, loading } = useAccountsPipeline();
  return (
    <SmartTable
      accounts={accounts}
      loading={loading}
      callerUid={user?.uid ?? null}
      callerEmail={user?.email ?? null}
    />
  );
}

// ---------------------------------------------------------------------------
// Dense view — original tabular layout, kept verbatim as a secondary view.
// ---------------------------------------------------------------------------

interface AccountRow extends AccountDoc {
  id: string;
}

type ColumnKey =
  | 'momentum'
  | 'strength'
  | 'economicBuyer'
  | 'economicBuyerSignedOff'
  | 'useCase'
  | 'tentativeLaunchDateAgreed'
  | 'budgetApprovedByEB'
  | 'potentialBlockers'
  | 'champion'
  | 'legalContact'
  | 'msaStatus'
  | 'procurementContact'
  | 'vendorRegistration'
  | 'agreedNextSteps'
  | 'callCount'
  | 'lastCallAt'
  | 'updatedAt';

const COLUMNS: { key: ColumnKey; label: string }[] = [
  { key: 'momentum', label: 'Momentum' },
  { key: 'strength', label: 'Strength' },
  { key: 'economicBuyer', label: 'Economic Buyer' },
  { key: 'economicBuyerSignedOff', label: 'EB Signed Off' },
  { key: 'useCase', label: 'Use Case' },
  { key: 'tentativeLaunchDateAgreed', label: 'Launch Date Agreed' },
  { key: 'budgetApprovedByEB', label: 'Budget Approved' },
  { key: 'potentialBlockers', label: 'Potential Blockers' },
  { key: 'champion', label: 'Champion' },
  { key: 'legalContact', label: 'Legal Contact' },
  { key: 'msaStatus', label: 'MSA / Terms' },
  { key: 'procurementContact', label: 'Procurement Contact' },
  { key: 'vendorRegistration', label: 'Vendor Reg.' },
  { key: 'agreedNextSteps', label: 'Agreed Next Steps' },
  { key: 'callCount', label: 'Calls' },
  { key: 'lastCallAt', label: 'Last activity' },
  { key: 'updatedAt', label: 'Updated' },
];

function statusChip(value?: string) {
  if (!value || value === 'Unknown') {
    return <span className="text-slate-400">Unknown</span>;
  }
  const lower = value.toLowerCase();
  let cls = 'bg-slate-100 text-slate-700';
  if (lower.startsWith('yes')) cls = 'bg-emerald-100 text-emerald-800';
  else if (lower.startsWith('no')) cls = 'bg-rose-100 text-rose-800';
  else if (lower.includes('partial') || lower.includes('progress'))
    cls = 'bg-amber-100 text-amber-800';
  return <span className={`chip ${cls}`}>{value}</span>;
}

function listCell(items?: string[]) {
  if (!items?.length) return <span className="text-slate-400">—</span>;
  return (
    <ul className="m-0 list-disc space-y-0.5 pl-4 text-xs leading-snug">
      {items.slice(0, 4).map((s, i) => (
        <li key={i}>{s}</li>
      ))}
      {items.length > 4 && (
        <li className="text-slate-400">+{items.length - 4} more</li>
      )}
    </ul>
  );
}

function textCell(value?: string) {
  if (!value || value === 'Unknown') {
    return <span className="text-slate-400">Unknown</span>;
  }
  return <span className="line-clamp-3 text-xs">{value}</span>;
}

function renderCell(account: AccountRow, key: ColumnKey) {
  const t = (account.dealTable ?? {}) as Partial<DealTable>;
  switch (key) {
    case 'momentum':
      return <ScoreChip score={account.momentum} kind="momentum" />;
    case 'strength':
      return <ScoreChip score={account.strength} kind="strength" />;
    case 'economicBuyer':
      return textCell(t.economicBuyer);
    case 'economicBuyerSignedOff':
      return statusChip(t.economicBuyerSignedOff);
    case 'useCase':
      return textCell(t.useCase);
    case 'tentativeLaunchDateAgreed':
      return statusChip(t.tentativeLaunchDateAgreed);
    case 'budgetApprovedByEB':
      return textCell(t.budgetApprovedByEB);
    case 'potentialBlockers':
      return listCell(t.potentialBlockers);
    case 'champion':
      return textCell(t.champion);
    case 'legalContact':
      return textCell(t.legalContact);
    case 'msaStatus':
      return statusChip(t.msaStatus);
    case 'procurementContact':
      return textCell(t.procurementContact);
    case 'vendorRegistration':
      return statusChip(t.vendorRegistration);
    case 'agreedNextSteps':
      return listCell(t.agreedNextSteps);
    case 'callCount':
      return <span className="text-xs text-slate-700">{account.callCount ?? 0}</span>;
    case 'lastCallAt':
      return (
        <span className="text-xs text-slate-600">
          {account.lastCallAt?.toDate().toLocaleDateString() ?? '—'}
        </span>
      );
    case 'updatedAt':
      return (
        <span className="text-xs text-slate-500">
          {account.updatedAt?.toDate().toLocaleString() ?? '—'}
        </span>
      );
    default:
      return null;
  }
}

function DenseView() {
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'analyzed' | 'pending'>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const q = query(collection(db, 'accounts'), orderBy('updatedAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows: AccountRow[] = snap.docs.map((d) => {
          const data = d.data() as DocumentData;
          return { id: d.id, ...(data as AccountDoc) };
        });
        setAccounts(rows);
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  const filtered = useMemo(() => {
    let out = accounts;
    if (filter === 'analyzed') out = out.filter((a) => !!a.dealTable);
    else if (filter === 'pending') out = out.filter((a) => !a.dealTable);
    if (search.trim()) {
      const s = search.toLowerCase();
      out = out.filter(
        (a) =>
          a.name?.toLowerCase().includes(s) ||
          a.dealTable?.useCase?.toLowerCase().includes(s) ||
          a.dealTable?.economicBuyer?.toLowerCase().includes(s) ||
          a.dealTable?.champion?.toLowerCase().includes(s),
      );
    }
    return out;
  }, [accounts, filter, search]);

  const counts = useMemo(
    () => ({
      total: accounts.length,
      analyzed: accounts.filter((a) => !!a.dealTable).length,
    }),
    [accounts],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Pipeline</h1>
          <p className="text-sm text-slate-500">
            {counts.analyzed} of {counts.total} accounts analyzed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search account, buyer, champion…"
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
          />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as typeof filter)}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm"
          >
            <option value="all">All ({counts.total})</option>
            <option value="analyzed">Analyzed ({counts.analyzed})</option>
            <option value="pending">Pending ({counts.total - counts.analyzed})</option>
          </select>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="sticky left-0 z-10 bg-slate-50 px-4 py-3 font-medium text-slate-600">
                  Account
                </th>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    className="whitespace-nowrap px-4 py-3 font-medium text-slate-600"
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {loading && (
                <tr>
                  <td className="px-4 py-6 text-slate-500" colSpan={COLUMNS.length + 1}>
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td
                    className="px-4 py-10 text-center text-slate-500"
                    colSpan={COLUMNS.length + 1}
                  >
                    No accounts match.
                  </td>
                </tr>
              )}
              {filtered.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50">
                  <td className="sticky left-0 z-10 bg-white px-4 py-3 align-top hover:bg-slate-50">
                    <Link
                      to={`/accounts/${a.id}`}
                      className="font-medium text-slate-900 hover:underline"
                    >
                      {a.name}
                    </Link>
                  </td>
                  {COLUMNS.map((c) => (
                    <td
                      key={c.key}
                      className="min-w-[140px] max-w-[260px] px-4 py-3 align-top"
                    >
                      {renderCell(a, c.key)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
